import * as React from 'react';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { useNotification } from './CustomNotification';
import {
  createOne as createEmployee,
  uploadContractImage,
  uploadProfileImage,
  validate as validateEmployee,
  type Employee,
} from '../data/employees';
import EmployeeForm, {
  type FormFieldValue,
  type EmployeeFormState,
} from './EmployeeForm';
import PageContainer from './PageContainer';
import { useLanguage } from '../../contexts/LanguageContext';

// Password generation utility
const generatePassword = () => {
  const length = 12;
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
};

const INITIAL_FORM_VALUES: Partial<EmployeeFormState['values']> = {
  role_name: 'Employee',
  isFullTime: true,
  employment_status: 'ACTIVE',
  contract_type: 'FULL_TIME',
  available_leave: 30,
  password: generatePassword(), // Generate initial password
  salary: 2500,
};

export default function EmployeeCreate() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const notifications = useNotification();

  const [formState, setFormState] = React.useState<EmployeeFormState>(() => ({
    values: INITIAL_FORM_VALUES,
    errors: {},
  }));
  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = React.useCallback(
    (newFormValues: Partial<EmployeeFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = React.useCallback(
    (newFormErrors: Partial<EmployeeFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = React.useCallback(
    (name: keyof EmployeeFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<EmployeeFormState['values']>) => {
        const { issues } = validateEmployee(values, true);
        const errorMessage = issues?.find((issue) => issue.path?.[0] === name)?.message;
        setFormErrors({
          ...formErrors,
          [name]: errorMessage ? t(errorMessage) : undefined,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues, t],
  );

  const handleFormReset = React.useCallback(() => {
    setFormValues(INITIAL_FORM_VALUES);
  }, [setFormValues]);

  const handleFormSubmit = React.useCallback(async () => {
    const { issues } = validateEmployee(formValues, true);
    const errors: Partial<EmployeeFormState['errors']> = {};

    if (issues && issues.length > 0) {
      issues.forEach((issue) => {
        if (issue.path?.[0]) {
          errors[issue.path[0] as keyof EmployeeFormState['errors']] = t(issue.message);
        }
      });
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});

    try {
      // Step 1: Create the employee
      const createdEmployee = await createEmployee(formValues as Omit<Employee, 'id'>);

      // Step 2: Upload profile image if provided
      const profileImage = formValues.profile_image as File | undefined;
      if (profileImage) {
        await uploadProfileImage(createdEmployee.id, profileImage);
      }

      // Step 3: Upload contract image if provided
      const contractImage = formValues.contract_image as File | undefined;
      if (contractImage) {
        await uploadContractImage(createdEmployee.id, contractImage);
      }

      notifications.show(t('messages.createSuccess'), {
        severity: 'success',
        autoHideDuration: 3000,
      });

      navigate(`/employees/${createdEmployee.id}`);
    } catch (createError) {
      notifications.show(
        `${t('messages.errorOccurred')}: ${(createError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      throw createError;
    }
  }, [formValues, navigate, notifications, setFormErrors, t]);

  const { direction } = useLanguage();
  const isRtl = direction === 'rtl';

  const handleBack = React.useCallback(() => {
    navigate('/employees');
  }, [navigate]);

  return (
    <PageContainer
      breadcrumbs={[{ title: t('employees.title'), path: '/employees' }, { title: t('employees.create') }]}
      backButton={
        <IconButton
          onClick={handleBack}
          sx={(theme) => ({
            backgroundColor: 'transparent',
            borderRadius: '50%',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : '#31394a',
              color: theme.palette.mode === 'dark'
                ? theme.palette.text.primary
                : 'white'
            },
          })}
          aria-label="back"
        >
          {isRtl ? <ArrowForward /> : <ArrowBackIcon />}
        </IconButton>
      }
    >
      <EmployeeForm
        formState={formState}
        onFieldChange={handleFormFieldChange}
        onSubmit={handleFormSubmit}
        onReset={handleFormReset}
        submitButtonLabel={t('common.create')}
        isCreateMode={true}
      />
    </PageContainer>
  );
}
